import { createElement } from '../utils/dom.js';

const DEBOUNCE_MS = 200;

/**
 * Met en surbrillance la partie correspondant à la requête dans le texte.
 * @param {string} text
 * @param {string} query
 * @returns {string}
 */
function highlightMatch(text, query) {
  if (!query) return text;

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const index = lowerText.indexOf(lowerQuery);

  if (index === -1) return text;

  const before = text.slice(0, index);
  const match = text.slice(index, index + query.length);
  const after = text.slice(index + query.length);

  return `${before}<span class="autocomplete__match">${match}</span>${after}`;
}

/**
 * Attache une autocomplétion à un champ input.
 * @param {{ input: HTMLInputElement, onSearch: (query: string) => Promise<Array>, onSelect: (item: object) => void }} options
 * @returns {{ destroy: () => void }}
 */
export function createAutocomplete({ input, onSearch, onSelect }) {
  const wrapper = createElement('div', { className: 'autocomplete' });
  input.parentNode?.insertBefore(wrapper, input);
  wrapper.appendChild(input);

  const list = createElement('div', {
    className: 'autocomplete__list',
    attrs: { role: 'listbox' },
  });
  wrapper.appendChild(list);

  let results = [];
  let activeIndex = -1;
  let debounceTimer = null;
  let currentQuery = '';

  /**
   * Ferme le dropdown.
   */
  function close() {
    wrapper.classList.remove('autocomplete--open');
    activeIndex = -1;
    list.innerHTML = '';
    results = [];
  }

  /**
   * Met à jour l'élément actif dans la liste.
   */
  function updateActiveItem() {
    const items = list.querySelectorAll('.autocomplete__item');
    items.forEach((item, i) => {
      item.classList.toggle('autocomplete__item--active', i === activeIndex);
    });

    if (activeIndex >= 0 && items[activeIndex]) {
      items[activeIndex].scrollIntoView({ block: 'nearest' });
    }
  }

  /**
   * Affiche les résultats de recherche.
   * @param {Array} items
   * @param {string} query
   */
  function renderResults(items, query) {
    list.innerHTML = '';
    results = items;
    activeIndex = -1;

    if (items.length === 0) {
      list.appendChild(createElement('div', {
        className: 'autocomplete__empty',
        text: query ? 'Aucun résultat' : '',
      }));
    } else {
      items.forEach((item, index) => {
        const btn = createElement('button', {
          className: 'autocomplete__item',
          attrs: { type: 'button', role: 'option' },
          html: highlightMatch(item.name, query),
          events: {
            click: () => {
              onSelect(item);
              close();
              input.value = item.name;
            },
            mouseenter: () => {
              activeIndex = index;
              updateActiveItem();
            },
          },
        });
        list.appendChild(btn);
      });
    }

    wrapper.classList.add('autocomplete--open');
  }

  /**
   * Lance une recherche avec debounce.
   */
  async function search() {
    const query = input.value.trim();
    currentQuery = query;

    if (!query) {
      close();
      return;
    }

    try {
      const items = await onSearch(query);
      if (currentQuery === query) {
        renderResults(items, query);
      }
    } catch {
      close();
    }
  }

  const onInput = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(search, DEBOUNCE_MS);
  };

  const onKeyDown = (e) => {
    if (!wrapper.classList.contains('autocomplete--open')) {
      if (e.key === 'ArrowDown' && input.value.trim()) {
        search();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        activeIndex = Math.min(activeIndex + 1, results.length - 1);
        updateActiveItem();
        break;
      case 'ArrowUp':
        e.preventDefault();
        activeIndex = Math.max(activeIndex - 1, 0);
        updateActiveItem();
        break;
      case 'Enter':
        if (activeIndex >= 0 && results[activeIndex]) {
          e.preventDefault();
          onSelect(results[activeIndex]);
          close();
          input.value = results[activeIndex].name;
        }
        break;
      case 'Escape':
        e.preventDefault();
        close();
        break;
      default:
        break;
    }
  };

  const onClickOutside = (e) => {
    if (!wrapper.contains(e.target)) {
      close();
    }
  };

  input.addEventListener('input', onInput);
  input.addEventListener('keydown', onKeyDown);
  document.addEventListener('click', onClickOutside);

  return {
    destroy() {
      clearTimeout(debounceTimer);
      input.removeEventListener('input', onInput);
      input.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('click', onClickOutside);
      wrapper.parentNode?.insertBefore(input, wrapper);
      wrapper.remove();
    },
  };
}
